import React, { useState } from 'react'
import { useQuery } from '@apollo/client'
import { gql } from '~/__generated__'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/ui/select'
import { Badge } from '~/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/ui/tabs'
import { MapPin, Globe, Clock, Activity, Users, BarChart3, Map } from 'lucide-react'
import { LocationGrouping } from '~/__generated__/graphql'

const LOCATION_ANALYTICS_QUERY = gql(`
  query LocationAnalytics(
    $organizationId: ID
    $zoneId: ID
    $startDate: ISO8601Date
    $endDate: ISO8601Date
    $days: Int
    $country: String
    $city: String
    $timezone: String
    $groupBy: LocationGrouping
    $limit: Int
  ) {
    locationAnalytics(
      organizationId: $organizationId
      zoneId: $zoneId
      startDate: $startDate
      endDate: $endDate
      days: $days
      country: $country
      city: $city
      timezone: $timezone
      groupBy: $groupBy
      limit: $limit
    ) {
      country
      city
      region
      locationDisplayName
      latitude
      longitude
      timezone
      totalEvents
      uniqueTracks
      uniqueDevices
      listeningTimeSeconds
      listeningTimeHours
      topTracks {
        trackId
        trackTitle
        trackArtist
        playCount
      }
    }
  }
`)

const PLAYBACK_ANALYTICS_QUERY = gql(`
  query PlaybackAnalyticsWithLocation(
    $organizationId: ID
    $zoneId: ID
    $startDate: ISO8601Date
    $endDate: ISO8601Date
    $days: Int
  ) {
    playbackAnalytics(
      organizationId: $organizationId
      zoneId: $zoneId
      startDate: $startDate
      endDate: $endDate
      days: $days
    ) {
      deviceLocations {
        deviceId
        deviceName
        latitude
        longitude
        city
        country
        timezone
        locationDisplayName
        lastLocationUpdate
        totalEvents
        listeningTimeHours
        uniqueTracksPlayed
        status
      }
    }
  }
`)

interface LocationAnalyticsDashboardProps {
  organizationId?: string
  zoneId?: string
  zoneName?: string
}

export const LocationAnalyticsDashboard: React.FC<LocationAnalyticsDashboardProps> = ({
  organizationId,
  zoneId,
  zoneName,
}) => {
  const [groupBy, setGroupBy] = useState<'COUNTRY' | 'CITY' | 'TIMEZONE' | 'REGION'>('COUNTRY')
  const [days, setDays] = useState(30)
  const [selectedCountry, setSelectedCountry] = useState<string>('')
  const [selectedCity, setSelectedCity] = useState<string>('')
  const [activeTab, setActiveTab] = useState('overview')

  const { data: locationData, loading: locationLoading } = useQuery(LOCATION_ANALYTICS_QUERY, {
    variables: {
      organizationId,
      zoneId,
      days,
      groupBy: groupBy as LocationGrouping,
      country: (selectedCountry === 'all' ? undefined : selectedCountry) || undefined,
      city: (selectedCity === 'all' ? undefined : selectedCity) || undefined,
      limit: 50,
    },
    fetchPolicy: 'cache-and-network',
  })

  const { data: deviceLocationData, loading: deviceLoading } = useQuery(PLAYBACK_ANALYTICS_QUERY, {
    variables: {
      organizationId,
      zoneId,
      days,
    },
    fetchPolicy: 'cache-and-network',
  })

  const locationAnalytics = locationData?.locationAnalytics || []
  const deviceLocations = deviceLocationData?.playbackAnalytics?.deviceLocations || []

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)

    if (hours > 0) {
      return `${hours}h ${minutes}m`
    }
    return `${minutes}m`
  }

  const getUniqueCountries = () => {
    const countries = new Set(locationAnalytics.map((loc: any) => loc.country).filter(Boolean))
    return Array.from(countries).sort()
  }

  const getUniqueCities = () => {
    const cities = new Set(locationAnalytics.map((loc: any) => loc.city).filter(Boolean))
    return Array.from(cities).sort()
  }

  const totalStats = locationAnalytics.reduce(
    (acc: any, location: any) => ({
      totalEvents: acc.totalEvents + location.totalEvents,
      totalListeningHours: acc.totalListeningHours + location.listeningTimeHours,
      uniqueDevices: Math.max(acc.uniqueDevices, location.uniqueDevices),
      uniqueTracks: Math.max(acc.uniqueTracks, location.uniqueTracks),
    }),
    { totalEvents: 0, totalListeningHours: 0, uniqueDevices: 0, uniqueTracks: 0 }
  )

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Location Analytics
            {zoneName && <Badge variant="outline">{zoneName}</Badge>}
          </CardTitle>
          <CardDescription>
            Geographic breakdown of music listening patterns and device locations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="min-w-48 flex-1">
              <label className="mb-2 block text-sm font-medium">Group By</label>
              <Select value={groupBy} onValueChange={(value: any) => setGroupBy(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="COUNTRY">Country</SelectItem>
                  <SelectItem value="CITY">City</SelectItem>
                  <SelectItem value="REGION">Region/State</SelectItem>
                  <SelectItem value="TIMEZONE">Timezone</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-32 flex-1">
              <label className="mb-2 block text-sm font-medium">Time Period</label>
              <Select value={days.toString()} onValueChange={(value) => setDays(parseInt(value))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="365">Last year</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {groupBy !== 'COUNTRY' && (
              <div className="min-w-48 flex-1">
                <label className="mb-2 block text-sm font-medium">Filter by Country</label>
                <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                  <SelectTrigger>
                    <SelectValue placeholder="All countries" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All countries</SelectItem>
                    {getUniqueCountries().map((country: any) => (
                      <SelectItem key={country} value={country}>
                        {country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {groupBy === 'CITY' && (
              <div className="min-w-48 flex-1">
                <label className="mb-2 block text-sm font-medium">Filter by City</label>
                <Select value={selectedCity} onValueChange={setSelectedCity}>
                  <SelectTrigger>
                    <SelectValue placeholder="All cities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All cities</SelectItem>
                    {getUniqueCities().map((city: any) => (
                      <SelectItem key={city} value={city}>
                        {city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Locations</p>
                <p className="text-2xl font-bold">{locationAnalytics.length}</p>
              </div>
              <MapPin className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Listening Time</p>
                <p className="text-2xl font-bold">{Math.round(totalStats.totalListeningHours)}h</p>
              </div>
              <Clock className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Devices</p>
                <p className="text-2xl font-bold">{deviceLocations.length}</p>
              </div>
              <Users className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Events</p>
                <p className="text-2xl font-bold">{totalStats.totalEvents.toLocaleString()}</p>
              </div>
              <Activity className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Location Overview
          </TabsTrigger>
          <TabsTrigger value="devices" className="flex items-center gap-2">
            <Map className="h-4 w-4" />
            Device Locations
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {/* Location Analytics Grid */}
          <Card>
            <CardHeader>
              <CardTitle>Listening by {groupBy.toLowerCase()}</CardTitle>
              <CardDescription>
                Music listening activity grouped by {groupBy.toLowerCase()}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {locationLoading ? (
                <div className="space-y-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="h-20 rounded-lg bg-gray-200"></div>
                    </div>
                  ))}
                </div>
              ) : locationAnalytics.length === 0 ? (
                <div className="py-8 text-center text-gray-500">
                  No location data available for the selected period
                </div>
              ) : (
                <div className="max-h-96 space-y-4 overflow-y-auto">
                  {locationAnalytics.map((location: any, index: number) => (
                    <div key={index} className="rounded-lg border p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="mb-2 flex items-center gap-2">
                            <h3 className="text-lg font-semibold">
                              {location.locationDisplayName}
                            </h3>
                            {location.timezone && (
                              <Badge variant="outline" className="text-xs">
                                {location.timezone}
                              </Badge>
                            )}
                          </div>

                          <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
                            <div>
                              <p className="text-gray-600">Listening Time</p>
                              <p className="font-medium">
                                {formatDuration(location.listeningTimeSeconds)}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-600">Events</p>
                              <p className="font-medium">{location.totalEvents.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Unique Tracks</p>
                              <p className="font-medium">{location.uniqueTracks}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Devices</p>
                              <p className="font-medium">{location.uniqueDevices}</p>
                            </div>
                          </div>

                          {location.topTracks && location.topTracks.length > 0 && (
                            <div className="mt-3">
                              <p className="mb-1 text-sm text-gray-600">Top Tracks:</p>
                              <div className="flex flex-wrap gap-1">
                                {location.topTracks
                                  .slice(0, 3)
                                  .map((track: any, trackIndex: number) => (
                                    <Badge key={trackIndex} variant="secondary" className="text-xs">
                                      {track.trackTitle} ({track.playCount})
                                    </Badge>
                                  ))}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="text-right">
                          <div className="text-2xl font-bold text-blue-600">
                            {Math.round(location.listeningTimeHours)}h
                          </div>
                          {location.latitude && location.longitude && (
                            <p className="mt-1 text-xs text-gray-500">
                              {location.latitude.toFixed(2)}, {location.longitude.toFixed(2)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="devices" className="space-y-4">
          {/* Device Locations */}
          <Card>
            <CardHeader>
              <CardTitle>Device Locations</CardTitle>
              <CardDescription>Current known locations of active devices</CardDescription>
            </CardHeader>
            <CardContent>
              {deviceLoading ? (
                <div className="space-y-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="h-16 rounded-lg bg-gray-200"></div>
                    </div>
                  ))}
                </div>
              ) : deviceLocations.length === 0 ? (
                <div className="py-8 text-center text-gray-500">
                  No device location data available
                </div>
              ) : (
                <div className="max-h-96 space-y-4 overflow-y-auto">
                  {deviceLocations.map((device: any, index: number) => (
                    <div key={index} className="rounded-lg border p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="mb-2 flex items-center gap-2">
                            <h3 className="font-semibold">
                              {device.deviceName || `Device ${device.deviceId.slice(0, 8)}`}
                            </h3>
                            <Badge
                              variant={device.status === 'online' ? 'default' : 'secondary'}
                              className="text-xs"
                            >
                              {device.status || 'unknown'}
                            </Badge>
                          </div>

                          <div className="mb-2 flex items-center gap-2 text-sm text-gray-600">
                            <MapPin className="h-4 w-4" />
                            <span>{device.locationDisplayName}</span>
                            {device.timezone && (
                              <Badge variant="outline" className="text-xs">
                                {device.timezone}
                              </Badge>
                            )}
                          </div>

                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <p className="text-gray-600">Listening Time</p>
                              <p className="font-medium">
                                {Math.round(device.listeningTimeHours)}h
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-600">Events</p>
                              <p className="font-medium">{device.totalEvents}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Unique Tracks</p>
                              <p className="font-medium">{device.uniqueTracksPlayed}</p>
                            </div>
                          </div>
                        </div>

                        <div className="text-right text-sm">
                          {device.latitude && device.longitude && (
                            <p className="text-gray-500">
                              {device.latitude.toFixed(2)}, {device.longitude.toFixed(2)}
                            </p>
                          )}
                          {device.lastLocationUpdate && (
                            <p className="mt-1 text-xs text-gray-400">
                              Updated: {new Date(device.lastLocationUpdate).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
